import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api, extractError, API_BASE } from "@/lib/api";
import { useCurrency } from "@/lib/currency";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import ImageUpload from "@/components/ImageUpload";
import { CheckCircle2, CreditCard, Lock, BedDouble, Car, Landmark, Copy, Clock, Zap } from "lucide-react";

async function publicUpload(file) {
  const token = localStorage.getItem("mbk_token");
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${API_BASE}/uploads`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: fd,
  });
  if (!res.ok) throw new Error("upload failed");
  return await res.json();
}

const steps = ["Détails", "Documents", "Paiement", "Confirmation"];

export default function Booking() {
  const { fmt } = useCurrency();
  const params = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  // Detect resource kind from URL path: /booking/lodging/:id  OR  /booking/:vehicleId
  const isLodging = location.pathname.startsWith("/booking/lodging/");
  const resourceId = isLodging ? params.lodgingId : params.vehicleId;

  const [resource, setResource] = useState(null);
  const [step, setStep] = useState(0);
  const [booking, setBooking] = useState(null);
  const [paid, setPaid] = useState(false);
  const [payMethod, setPayMethod] = useState("stripe"); // "stripe" | "transfer" | "paystack"
  const [transferRef, setTransferRef] = useState("");
  const [transferProof, setTransferProof] = useState("");
  const [providers, setProviders] = useState({ stripe: true, paystack: false, transfer: true });

  useEffect(() => {
    api.get("/payments/providers").then((r) => setProviders(r.data)).catch(() => {});
  }, []);
  const [form, setForm] = useState({
    first_name: user?.full_name?.split(" ")[0] || "",
    last_name: user?.full_name?.split(" ").slice(1).join(" ") || "",
    email: user?.email || "",
    phone: user?.phone || "",
    country: "",
    flight_number: "",
    pickup_location: "",
    with_driver: false,
    start_date: "",
    end_date: "",
    guests: 1,
    special_requests: "",
    driver_license_url: "",
    passport_url: "",
    id_card_url: "",
  });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const path = isLodging ? `/lodgings/${resourceId}` : `/vehicles/${resourceId}`;
    api.get(path).then((r) => setResource(r.data));
  }, [isLodging, resourceId]);

  const upd = (k, val) => setForm((f) => ({ ...f, [k]: val }));

  const fetchQuote = async () => {
    if (!form.start_date || !form.end_date) return;
    try {
      const payload = isLodging
        ? { lodging_id: resourceId, start_date: form.start_date, end_date: form.end_date }
        : { vehicle_id: resourceId, start_date: form.start_date, end_date: form.end_date, with_driver: form.with_driver };
      const { data } = await api.post("/bookings/quote", payload);
      setQuote(data);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { fetchQuote(); /* eslint-disable-next-line */ }, [form.start_date, form.end_date, form.with_driver]);

  const canProceedFromDetails = () =>
    form.first_name && form.last_name && form.email && form.phone && form.country && form.start_date && form.end_date &&
    (isLodging ? form.guests > 0 : form.pickup_location);

  const createDraft = async () => {
    setBusy(true);
    try {
      const payload = isLodging
        ? { ...form, lodging_id: resourceId, pickup_location: resource?.address || resource?.city || "" }
        : { ...form, vehicle_id: resourceId };
      const { data } = await api.post("/bookings", payload);
      setBooking(data);
      setStep(1);
    } catch (e) {
      toast.error(extractError(e, "Erreur lors de la création de la réservation"));
    } finally { setBusy(false); }
  };

  const payDeposit = async () => {
    if (!booking) return;
    setBusy(true);
    try {
      // Real Stripe Checkout flow: request session URL then redirect
      const origin = window.location.origin;
      const { data } = await api.post("/payments/stripe/checkout", {
        booking_id: booking.id,
        origin_url: origin,
        mode: "deposit",
      });
      if (!data?.checkout_url) throw new Error("checkout_url manquant");
      // Persist booking id for the success page fallback
      try { sessionStorage.setItem("mbk_last_booking_id", booking.id); } catch {}
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(extractError(e, "Impossible d'initier le paiement par carte. Veuillez réessayer."));
      setBusy(false);
    }
  };

  const submitTransfer = async () => {
    if (!booking) return;
    if (!transferRef.trim()) { toast.error("Veuillez indiquer la référence de votre virement."); return; }
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/pay-by-transfer`, {
        transfer_reference: transferRef.trim(),
        proof_url: transferProof || "",
        mode: "deposit",
      });
      setPaid(true);
      setStep(3);
      toast.success("Virement déclaré. Vous recevrez une confirmation dès validation par MOBIKAR.");
    } catch (e) {
      toast.error(extractError(e, "Erreur lors de la déclaration du virement"));
    } finally { setBusy(false); }
  };

  const payPaystack = async () => {
    if (!booking) return;
    setBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/payments/paystack/checkout", {
        booking_id: booking.id,
        origin_url: origin,
        mode: "deposit",
      });
      if (!data?.authorization_url) throw new Error("authorization_url manquant");
      try { sessionStorage.setItem("mbk_last_booking_id", booking.id); } catch {}
      window.location.assign(data.authorization_url);
    } catch (e) {
      toast.error(extractError(e, "Impossible d'initier le paiement. Veuillez réessayer."));
      setBusy(false);
    }
  };

  const copyToClipboard = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copié"); } catch { toast.error("Impossible de copier"); }
  };

  const finalize = () => {
    if (!paid) { toast.error("Veuillez payer l'acompte pour finaliser."); return; }
    navigate(`/confirmation/${booking.id}`);
  };

  if (!resource) return (
    <div className="min-h-screen bg-[#f8f9fa]"><SiteHeader /><div className="p-24 text-center">Chargement…</div></div>
  );

  const unitLabel = isLodging ? "nuits" : "jours";
  const nightlyOrDaily = isLodging ? resource.price_per_night : resource.price_per_day;
  const resourceIcon = isLodging ? BedDouble : Car;
  const ResourceIcon = resourceIcon;

  return (
    <div className="min-h-screen bg-[#f8f9fa]" data-testid={`booking-page-${isLodging ? "lodging" : "vehicle"}`}>
      <SiteHeader />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16">
        <div className="mb-8">
          <div className="flex justify-between text-xs font-semibold text-gray-500 mb-2">
            {steps.map((s, i) => (<span key={s} className={i <= step ? "text-[#0e7a3f]" : ""}>{i + 1}. {s}</span>))}
          </div>
          <Progress value={((step + 1) / steps.length) * 100} className="h-2 bg-gray-100" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-6 md:p-8 border border-gray-100 mbk-card-shadow">
              {step === 0 && (
                <div>
                  <h2 className="font-display text-2xl font-black text-[#0a1e3f]">Vos informations</h2>
                  <p className="text-sm text-gray-500 mt-1">Renseignez les détails de votre réservation.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    <Field label="Prénom" testId="fld-first"><Input value={form.first_name} onChange={(e) => upd("first_name", e.target.value)} /></Field>
                    <Field label="Nom" testId="fld-last"><Input value={form.last_name} onChange={(e) => upd("last_name", e.target.value)} /></Field>
                    <Field label="Email" testId="fld-email"><Input type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} /></Field>
                    <Field label="Téléphone" testId="fld-phone"><Input value={form.phone} onChange={(e) => upd("phone", e.target.value)} /></Field>
                    <Field label="Pays de résidence" testId="fld-country"><Input value={form.country} onChange={(e) => upd("country", e.target.value)} /></Field>
                    {!isLodging && <Field label="Numéro de vol (optionnel)" testId="fld-flight"><Input value={form.flight_number} onChange={(e) => upd("flight_number", e.target.value)} /></Field>}
                    <Field label={isLodging ? "Date d'arrivée" : "Date d'arrivée"} testId="fld-start"><Input type="date" value={form.start_date} onChange={(e) => upd("start_date", e.target.value)} /></Field>
                    <Field label={isLodging ? "Date de départ" : "Date de départ"} testId="fld-end"><Input type="date" value={form.end_date} onChange={(e) => upd("end_date", e.target.value)} /></Field>
                    {isLodging ? (
                      <>
                        <Field label="Nombre de voyageurs" testId="fld-guests"><Input type="number" min="1" max={resource.max_guests || 20} value={form.guests} onChange={(e) => upd("guests", parseInt(e.target.value) || 1)} /></Field>
                        <div className="md:col-span-2">
                          <Field label="Remarques particulières (optionnel)" testId="fld-special"><Input value={form.special_requests} onChange={(e) => upd("special_requests", e.target.value)} placeholder="Arrivée tardive, besoin d'un lit bébé, etc." /></Field>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="md:col-span-2">
                          <Field label="Lieu de remise du véhicule" testId="fld-pickup"><Input value={form.pickup_location} onChange={(e) => upd("pickup_location", e.target.value)} placeholder="Aéroport, Hôtel, Adresse…" /></Field>
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Type de service</Label>
                          <RadioGroup value={form.with_driver ? "yes" : "no"} onValueChange={(v) => upd("with_driver", v === "yes")} className="flex gap-6 mt-3">
                            <div className="flex items-center gap-2"><RadioGroupItem value="no" id="d-no" data-testid="drv-no" /><Label htmlFor="d-no">Sans chauffeur</Label></div>
                            <div className="flex items-center gap-2"><RadioGroupItem value="yes" id="d-yes" data-testid="drv-yes" /><Label htmlFor="d-yes">Avec chauffeur</Label></div>
                          </RadioGroup>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              {step === 1 && (
                <div>
                  <h2 className="font-display text-2xl font-black text-[#0a1e3f]">Documents</h2>
                  <p className="text-sm text-gray-500 mt-1">Téléchargez vos documents depuis votre téléphone ou ordinateur.</p>
                  <div className="grid grid-cols-1 gap-5 mt-6">
                    {!isLodging && (
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-2 block">Permis de conduire</Label>
                        <ImageUpload value={form.driver_license_url} onChange={(v) => upd("driver_license_url", v)} label="Permis" testId="doc-license" />
                      </div>
                    )}
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-2 block">Passeport {isLodging ? "" : "(facultatif)"}</Label>
                      <ImageUpload value={form.passport_url} onChange={(v) => upd("passport_url", v)} label="Passeport" testId="doc-passport" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-2 block">Pièce d'identité</Label>
                      <ImageUpload value={form.id_card_url} onChange={(v) => upd("id_card_url", v)} label="Pièce d'identité" testId="doc-idcard" />
                    </div>
                  </div>
                </div>
              )}
              {step === 2 && (
                <div>
                  <h2 className="font-display text-2xl font-black text-[#0a1e3f]">Paiement de l'acompte</h2>
                  <p className="text-sm text-gray-500 mt-1">Choisissez votre moyen de paiement. Un contrat PDF vous sera automatiquement envoyé par email dès validation.</p>
                  <div className="mt-6 grid grid-cols-1 gap-4">
                    {/* Choix : Stripe */}
                    <button type="button" data-testid="pay-method-stripe" onClick={() => setPayMethod("stripe")} className={`text-left border-2 rounded-2xl p-4 flex items-center gap-3 transition ${payMethod === "stripe" ? "border-[#0a1e3f] bg-[#0a1e3f]/5" : "border-gray-200 hover:border-gray-300"}`}>
                      <div className={`h-4 w-4 rounded-full ${payMethod === "stripe" ? "bg-[#0a1e3f]" : "border-2 border-gray-300"} flex items-center justify-center`}>
                        {payMethod === "stripe" && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                      </div>
                      <CreditCard className="h-5 w-5 text-[#0a1e3f]" />
                      <div><div className="font-semibold text-sm text-[#0a1e3f]">Carte bancaire</div><div className="text-xs text-gray-500">Visa · Mastercard · Amex · Paiement sécurisé, confirmation immédiate</div></div>
                    </button>

                    {/* Choix : Transfert bancaire */}
                    {providers.transfer !== false && (
                      <button type="button" data-testid="pay-method-transfer" onClick={() => setPayMethod("transfer")} className={`text-left border-2 rounded-2xl p-4 flex items-center gap-3 transition ${payMethod === "transfer" ? "border-[#0a1e3f] bg-[#0a1e3f]/5" : "border-gray-200 hover:border-gray-300"}`}>
                        <div className={`h-4 w-4 rounded-full ${payMethod === "transfer" ? "bg-[#0a1e3f]" : "border-2 border-gray-300"} flex items-center justify-center`}>
                          {payMethod === "transfer" && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                        </div>
                        <Landmark className="h-5 w-5 text-[#0a1e3f]" />
                        <div><div className="font-semibold text-sm text-[#0a1e3f]">Virement bancaire</div><div className="text-xs text-gray-500">Effectuez le virement puis soumettez la référence — validation manuelle sous 24 h ouvrées</div></div>
                      </button>
                    )}

                    {/* Choix : Paystack */}
                    {providers.paystack === true && (
                      <button type="button" data-testid="pay-method-paystack" onClick={() => setPayMethod("paystack")} className={`text-left border-2 rounded-2xl p-4 flex items-center gap-3 transition ${payMethod === "paystack" ? "border-[#0a1e3f] bg-[#0a1e3f]/5" : "border-gray-200 hover:border-gray-300"}`}>
                        <div className={`h-4 w-4 rounded-full ${payMethod === "paystack" ? "bg-[#0a1e3f]" : "border-2 border-gray-300"} flex items-center justify-center`}>
                          {payMethod === "paystack" && <div className="h-1.5 w-1.5 rounded-full bg-white" />}
                        </div>
                        <Zap className="h-5 w-5 text-[#0e7a3f]" />
                        <div><div className="font-semibold text-sm text-[#0a1e3f]">Paiement Afrique</div><div className="text-xs text-gray-500">Carte, Mobile Money, USSD, virement · Afrique de l'Ouest & Est · confirmation immédiate</div></div>
                      </button>
                    )}

                    {/* Mobile Money — bientôt */}
                    <div className="border border-gray-200 rounded-2xl p-4 flex items-center gap-3 opacity-60">
                      <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                      <div><div className="font-semibold text-sm text-[#0a1e3f]">Mobile Money</div><div className="text-xs text-gray-500">Orange Money · MTN Money · Wave (bientôt disponible)</div></div>
                    </div>

                    {/* Bloc infos bancaires (visible seulement en mode virement) */}
                    {payMethod === "transfer" && (
                      <div className="bg-white border-2 border-[#0e7a3f]/30 rounded-2xl p-5 space-y-4" data-testid="transfer-block">
                        <div className="flex items-center gap-2">
                          <Landmark className="h-5 w-5 text-[#0e7a3f]" />
                          <div className="font-display text-lg font-black text-[#0a1e3f]">Effectuez le virement</div>
                        </div>
                        <div className="rounded-xl bg-gray-50 border border-gray-100 divide-y divide-gray-100 text-sm">
                          <BankRow k="Bénéficiaire" v={settings?.transfer_beneficiary} onCopy={copyToClipboard} />
                          <BankRow k="Banque" v={settings?.transfer_bank_name} onCopy={copyToClipboard} />
                          <BankRow k="IBAN / N° de compte" v={settings?.transfer_iban} onCopy={copyToClipboard} mono />
                          <BankRow k="Code SWIFT / BIC" v={settings?.transfer_swift} onCopy={copyToClipboard} mono />
                          <BankRow k="Montant à virer" v={fmt(quote?.deposit || 0)} onCopy={copyToClipboard} strong />
                          <BankRow k="Référence à mentionner" v={booking?.reference || "—"} onCopy={copyToClipboard} mono strong />
                        </div>
                        {settings?.transfer_instructions && (
                          <div className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded-lg p-3">{settings.transfer_instructions}</div>
                        )}
                        <div className="pt-2 space-y-3">
                          <div>
                            <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">Référence / numéro de votre virement <span className="text-red-500">*</span></Label>
                            <Input data-testid="transfer-ref-input" className="mt-2" value={transferRef} onChange={(e) => setTransferRef(e.target.value)} placeholder="Ex : VIR-20260215-XXXX" />
                          </div>
                          <div>
                            <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-2 block">Justificatif (optionnel — capture / reçu bancaire)</Label>
                            <ImageUpload value={transferProof} onChange={setTransferProof} label="Justificatif" testId="transfer-proof" />
                          </div>
                        </div>
                        <div className="text-xs text-[#0a1e3f]/70 bg-[#0e7a3f]/5 rounded-lg p-3 flex items-start gap-2">
                          <Clock className="h-4 w-4 mt-0.5 shrink-0 text-[#0e7a3f]" />
                          <div>Après soumission, MOBIKAR vérifie la réception sous 24 h ouvrées. Vous recevrez un email de confirmation et votre contrat PDF dès validation.</div>
                        </div>
                      </div>
                    )}

                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900 flex items-start gap-2">
                      <Lock className="h-4 w-4 mt-0.5 shrink-0" />
                      <div>Montant à régler maintenant : <b className="text-base">{fmt(quote?.deposit || 0)}</b>. Le solde de <b>{fmt(quote?.balance || 0)}</b> sera dû à la remise.</div>
                    </div>
                  </div>
                </div>
              )}
              {step === 3 && (
                <div className="text-center py-4">
                  <div className="h-16 w-16 rounded-full bg-[#dcfce7] text-[#0e7a3f] flex items-center justify-center mx-auto"><CheckCircle2 className="h-8 w-8" /></div>
                  <h2 className="font-display text-2xl font-black text-[#0a1e3f] mt-4">
                    {payMethod === "transfer" ? "Virement enregistré !" : "Réservation confirmée !"}
                  </h2>
                  <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                    {payMethod === "transfer"
                      ? <>Votre déclaration de virement pour <b>{fmt(quote?.deposit || 0)}</b> a bien été enregistrée. Notre équipe vérifie la réception sous 24 h ouvrées et vous enverra le contrat PDF dès validation.</>
                      : <>Votre acompte de <b>{fmt(quote?.deposit || 0)}</b> a bien été enregistré. Un email de confirmation avec le contrat PDF vient de vous être envoyé.</>
                    }
                  </p>
                </div>
              )}

              <div className="mt-8 flex items-center justify-between gap-3">
                <Button variant="outline" disabled={step === 0 || step === 3 || busy} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="prev-step" className="rounded-full h-11 px-5">Précédent</Button>
                {step === 0 && (
                  <Button data-testid="to-docs" disabled={!canProceedFromDetails() || busy} onClick={createDraft} className="mbk-btn-primary h-11 px-6 text-white">
                    {busy ? "…" : "Continuer"}
                  </Button>
                )}
                {step === 1 && (
                  <Button data-testid="to-pay" onClick={() => setStep(2)} className="mbk-btn-primary h-11 px-6 text-white">Passer au paiement</Button>
                )}
                {step === 2 && (
                  payMethod === "transfer" ? (
                    <Button data-testid="submit-transfer" disabled={busy || !booking || !transferRef.trim()} onClick={submitTransfer} className="mbk-btn-primary h-12 px-6 text-white font-semibold">
                      <Landmark className="h-4 w-4 mr-2" /> {busy ? "Envoi…" : "Déclarer mon virement"}
                    </Button>
                  ) : payMethod === "paystack" ? (
                    <Button data-testid="pay-paystack" disabled={busy || !booking} onClick={payPaystack} className="mbk-btn-green h-12 px-6 text-white font-semibold">
                      <Zap className="h-4 w-4 mr-2" /> {busy ? "Redirection…" : `Confirmer et payer ${fmt(quote?.deposit || 0)}`}
                    </Button>
                  ) : (
                    <Button data-testid="pay-deposit" disabled={busy || !booking} onClick={payDeposit} className="mbk-btn-green h-12 px-6 text-white font-semibold">
                      <Lock className="h-4 w-4 mr-2" /> {busy ? "Traitement…" : `Confirmer et payer ${fmt(quote?.deposit || 0)}`}
                    </Button>
                  )
                )}
                {step === 3 && (
                  <Button data-testid="finalize" onClick={finalize} className="mbk-btn-primary h-11 px-6 text-white">Aller à la confirmation</Button>
                )}
              </div>
            </div>
          </div>

          <div>
            <div className="bg-white rounded-3xl p-6 border border-gray-100 mbk-card-shadow lg:sticky lg:top-24">
              <div className="text-xs uppercase tracking-widest text-gray-500 font-bold flex items-center gap-2">
                <ResourceIcon className="h-3.5 w-3.5 text-[#0e7a3f]" /> Récapitulatif
              </div>
              <div className="mt-4 flex items-center gap-3">
                <img src={(resource.photos && resource.photos[0]) || ""} alt="" className="h-16 w-24 object-cover rounded-xl bg-gray-100" />
                <div>
                  <div className="font-semibold text-[#0a1e3f]">{resource.name}</div>
                  <div className="text-xs text-gray-500">{resource.agency?.name}</div>
                </div>
              </div>
              <div className="mt-4 text-sm space-y-2">
                <RowKV k="Du" v={form.start_date || "-"} />
                <RowKV k="Au" v={form.end_date || "-"} />
                <RowKV k={unitLabel[0].toUpperCase() + unitLabel.slice(1)} v={quote?.days ?? 0} />
                <RowKV k={isLodging ? "Prix / nuit" : "Prix / jour"} v={fmt(quote?.daily || nightlyOrDaily || 0)} />
                {!isLodging && form.with_driver && quote?.driver_daily > 0 && <RowKV k="Chauffeur / jour" v={fmt(quote.driver_daily)} />}
                {isLodging && <RowKV k="Voyageurs" v={form.guests} />}
              </div>
              <div className="mt-4 pt-4 border-t border-gray-100 space-y-2">
                <RowKV k="Sous-total" v={fmt(quote?.subtotal || 0)} />
                <RowKV k={quote?.deposit_mode === "fixed" ? "Acompte (fixe)" : `Acompte (${quote?.deposit_percent || 30}%)`} v={fmt(quote?.deposit || 0)} strong />
                <RowKV k="Solde à la remise" v={fmt(quote?.balance || 0)} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const Field = ({ label, testId, children }) => (
  <div data-testid={testId}>
    <Label className="text-xs uppercase tracking-widest text-gray-500 font-bold">{label}</Label>
    <div className="mt-2">{children}</div>
  </div>
);

const RowKV = ({ k, v, strong }) => (
  <div className="flex justify-between text-sm">
    <span className="text-gray-500">{k}</span>
    <span className={strong ? "font-black text-[#0e7a3f]" : "font-semibold text-[#0a1e3f]"}>{v}</span>
  </div>
);

const BankRow = ({ k, v, onCopy, mono, strong }) => {
  const value = v || "—";
  const copyable = !!v && v !== "—";
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">{k}</div>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`${mono ? "font-mono" : ""} ${strong ? "font-black text-[#0e7a3f]" : "font-semibold text-[#0a1e3f]"} text-sm truncate`}>{value}</span>
        {copyable && (
          <button type="button" onClick={() => onCopy(v)} className="h-7 w-7 rounded-full bg-white border border-gray-200 hover:border-[#0a1e3f] text-gray-500 hover:text-[#0a1e3f] flex items-center justify-center shrink-0" title="Copier">
            <Copy className="h-3 w-3" />
          </button>
        )}
      </div>
    </div>
  );
};
